// Trigger sets for the groundworks batch — trenching & shoring, confined space
// entry, drainage installation and underground services. Sibling to the live
// excavation set in launch.ts; reuses its services/entry/water/plant/support/
// shift-inspection patterns, adapted per task.

import type { TriggerQuestion } from "./types";
import { UNDERGROUND_SERVICES, CONFINED_SPACE, PLANT_PEDESTRIAN, SILICA } from "./shared";

const FLOODING = "haz-excavation-flooding-and-groundwater-ingres-in-excavation";

export const GROUNDWORKS_TRIGGERS: Record<string, TriggerQuestion[]> = {
  "trenching-shoring": [
    {
      key: "support",
      label: "How will the trench be supported?",
      kind: "choice",
      options: ["Trench box / drag box", "Hydraulic waling & struts", "Battered / benched sides"],
      effects: {
        "Trench box / drag box": { scopeLine: "Trench support: proprietary trench boxes lowered or pushed from ground level and advanced ahead of the dig — no entry to an unsupported trench." },
        "Hydraulic waling & struts": { scopeLine: "Trench support: hydraulic waling frames and struts installed progressively with the dig per the temporary works design." },
        "Battered / benched sides": { scopeLine: "Trench support: sides battered or benched to a safe angle assessed by the competent person; box height standing proud where boxes are used." },
      },
    },
    {
      key: "services",
      label: "Are underground services present, or is their location unknown?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [UNDERGROUND_SERVICES], permitsAdd: ["Permit to dig"] },
        no: { hazardsOff: [UNDERGROUND_SERVICES], scopeLine: "No underground services along the trench line — confirmed against service drawings before excavation; the permit to dig still governs breaking ground." },
      },
      notSure: { hazardsOn: [UNDERGROUND_SERVICES], failCheck: "Underground services along the trench line confirmed" },
      followUps: {
        when: "yes",
        questions: [
          {
            key: "catScan",
            label: "Have a CAT/Genny scan and service-drawing review been completed?",
            kind: "yesno",
            effects: {
              yes: { scopeLine: "CAT/Genny scan and service-drawing review completed before excavation; hand digging within 500 mm of any service." },
              no: { failCheck: "CAT/Genny scan and service drawings confirmed" },
            },
          },
        ],
      },
    },
    {
      key: "entry",
      label: "Could the trench become a confined space (deep, enclosed or gas risk)?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [CONFINED_SPACE], permitsAdd: ["Confined space entry permit"] },
        no: { hazardsOff: [CONFINED_SPACE], permitsRemove: ["Confined space entry permit"], emergencyOff: ["Worker overcome"], scopeLine: "Trench does not meet confined space criteria — atmospheric testing and confined space entry controls are not required for this task." },
      },
      notSure: { hazardsOn: [CONFINED_SPACE], permitsAdd: ["Confined space entry permit"], failCheck: "Confined-space status of the trench confirmed" },
    },
    {
      key: "water",
      label: "Is water ingress or groundwater likely?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [FLOODING] },
        no: { hazardsOff: [FLOODING], emergencyOff: ["Rapid flooding"], scopeLine: "No groundwater or water ingress expected — dewatering controls are not required for this task." },
      },
      notSure: { hazardsOn: [FLOODING], failCheck: "Groundwater risk confirmed" },
    },
    {
      key: "lifting",
      label: "Will trench boxes or heavy components be lifted with the excavator?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: ["haz-excavation-crush-or-fall-during-trench-box-installation"], permitsAdd: ["Lifting plan for trench box installation (LOLER)"] },
        no: { hazardsOff: ["haz-excavation-crush-or-fall-during-trench-box-installation"], permitsRemove: ["Lifting plan for trench box installation (LOLER)"], scopeLine: "No lifting of trench boxes or heavy components — proprietary support is not craned in for this task." },
      },
      notSure: { hazardsOn: ["haz-excavation-crush-or-fall-during-trench-box-installation"], permitsAdd: ["Lifting plan for trench box installation (LOLER)"], failCheck: "Trench box lifting requirement confirmed" },
    },
    {
      key: "plant",
      label: "Will excavation plant or vehicles be used near the trench?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [PLANT_PEDESTRIAN] },
        no: { hazardsOff: [PLANT_PEDESTRIAN], scopeLine: "No plant or vehicles operating near the trench — plant/pedestrian segregation is managed by hand-dig methods for this task." },
      },
      notSure: { hazardsOn: [PLANT_PEDESTRIAN], failCheck: "Plant near the trench confirmed" },
    },
    {
      key: "breaking",
      label: "Will hard ground or surfacing be broken out (dust)?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [SILICA] },
        no: { hazardsOff: [SILICA], scopeLine: "No breaking-out of hard ground or surfacing — silica dust controls are not required for this task." },
      },
      notSure: { hazardsOn: [SILICA], failCheck: "Breaking-out / dust generation confirmed" },
    },
    {
      key: "shiftInspection",
      label: "Will a competent person inspect the trench supports at the start of each shift?",
      kind: "text",
      placeholder: "e.g. J Smith, named competent person, recorded each shift",
      requiredCheck: "Trench support inspection regime and competent person recorded",
      effects: { "*": { scopeLine: "Support inspection: {answer}. Inspected before entry each shift and after any event affecting stability, recorded." } },
    },
  ],

  "confined-space-entry": [
    {
      key: "avoidEntry",
      label: "Has the option to do the work without entering the space been ruled out?",
      kind: "yesno",
      effects: {
        yes: { scopeLine: "Entry cannot reasonably be avoided (CSR 1997 reg 4) — the residual entry is justified in the risk assessment." },
        no: { failCheck: "Avoidance of entry assessed under CSR 1997 reg 4" },
      },
      notSure: { failCheck: "Avoidance of entry assessed under CSR 1997 reg 4" },
    },
    {
      key: "rescuePlan",
      label: "Who provides the rescue and resuscitation capability (named team / role)?",
      kind: "text",
      placeholder: "e.g. on-site trained rescue team, tripod/winch non-entry retrieval",
      requiredCheck: "Confined space rescue team and method recorded (not the public fire service)",
      effects: { "*": { scopeLine: "Rescue arrangements: {answer}. In place, equipped and rehearsed before entry (CSR 1997 reg 5)." } },
    },
    {
      key: "atmosphere",
      label: "Will the atmosphere be tested before entry and monitored continuously inside?",
      kind: "yesno",
      effects: {
        yes: { scopeLine: "Atmosphere bump-tested then tested from the surface (O2, LEL, H2S, CO) before entry and monitored continuously inside; evacuate at any alarm or 10% LEL." },
        no: { failCheck: "Atmospheric testing and continuous monitoring confirmed" },
      },
      notSure: { failCheck: "Atmospheric testing and continuous monitoring confirmed" },
    },
    {
      key: "isolation",
      label: "Are there inlets, pumps or feeds that could engulf or start during the works?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: ["haz-confined-space-engulfment-from-free-flowing-solid-or-liquid"], permitsAdd: ["Isolation/lock-off permit"] },
        no: { hazardsOff: ["haz-confined-space-engulfment-from-free-flowing-solid-or-liquid"], permitsRemove: ["Isolation/lock-off permit"], emergencyOff: ["Engulfment"], scopeLine: "No inlets, pumps or feeds to the space — engulfment from free-flowing solids or liquid ingress is not a foreseeable risk for this entry." },
      },
      notSure: { hazardsOn: ["haz-confined-space-engulfment-from-free-flowing-solid-or-liquid"], permitsAdd: ["Isolation/lock-off permit"], failCheck: "Inlet / pump isolation requirement confirmed" },
    },
    {
      key: "verticalAccess",
      label: "Is access via a shaft or deep chamber where a fall is possible?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: ["haz-working-at-height-fall-from-height-during-installation-or-work-at-height"] },
        no: { hazardsOff: ["haz-working-at-height-fall-from-height-during-installation-or-work-at-height"], scopeLine: "Access is at or near ground level — fall-arrest on vertical access is not required for this entry." },
      },
      notSure: { hazardsOn: ["haz-working-at-height-fall-from-height-during-installation-or-work-at-height"], failCheck: "Vertical-access fall risk confirmed" },
    },
    {
      key: "contamination",
      label: "Is the space contaminated with sludge, sewage or chemical residues (COSHH)?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: ["haz-coshh-coshh-hazardou-substance-exposure"] },
        no: { hazardsOff: ["haz-coshh-coshh-hazardou-substance-exposure"], scopeLine: "Space is clean and free of sludge, sewage or chemical residues — COSHH contamination controls are not required for this entry." },
      },
      notSure: { hazardsOn: ["haz-coshh-coshh-hazardou-substance-exposure"], failCheck: "Contamination / COSHH residues confirmed" },
    },
  ],

  "drainage-installation": [
    {
      key: "liveSewer",
      label: "Will you connect into, or work near, a live foul sewer?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: ["haz-drainage-uncontrolled-discharge-connecting-to-live-sewer"], permitsAdd: ["Asset-owner agreement to connect into a live sewer"] },
        no: { hazardsOff: ["haz-drainage-uncontrolled-discharge-connecting-to-live-sewer"], permitsRemove: ["Asset-owner agreement to connect into a live sewer"], emergencyOff: ["Uncontrolled discharge"], scopeLine: "No connection into a live sewer — the run is connected dead or capped for later connection; live-flow controls are not required for this task." },
      },
      notSure: { hazardsOn: ["haz-drainage-uncontrolled-discharge-connecting-to-live-sewer"], permitsAdd: ["Asset-owner agreement to connect into a live sewer"], failCheck: "Live-sewer connection confirmed with the asset owner" },
    },
    {
      key: "services",
      label: "Are other underground services present, or is their location unknown?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [UNDERGROUND_SERVICES], permitsAdd: ["Permit to dig"] },
        no: { hazardsOff: [UNDERGROUND_SERVICES], scopeLine: "No other underground services on the drainage run — confirmed against service drawings before excavation; the permit to dig still governs breaking ground." },
      },
      notSure: { hazardsOn: [UNDERGROUND_SERVICES], failCheck: "Underground services on the drainage run confirmed" },
    },
    {
      key: "entry",
      label: "Will anyone enter an existing chamber, manhole or wet well (confined space)?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [CONFINED_SPACE], permitsAdd: ["Confined space entry permit"] },
        no: { hazardsOff: [CONFINED_SPACE], permitsRemove: ["Confined space entry permit"], emergencyOff: ["Worker overcome"], scopeLine: "No entry into existing chambers, manholes or wet wells — confined space entry controls are not required for this task." },
      },
      notSure: { hazardsOn: [CONFINED_SPACE], permitsAdd: ["Confined space entry permit"], failCheck: "Confined-space entry into existing chambers confirmed" },
    },
    {
      key: "trench",
      label: "Will the pipework be laid in a supported trench?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: ["haz-excavation-excavation-or-trench-collapse"], permitsAdd: ["Permit to dig"] },
        no: { hazardsOff: ["haz-excavation-excavation-or-trench-collapse"], scopeLine: "Pipework is laid at shallow depth not requiring trench support per the risk assessment — trench collapse controls are covered by the trenching RAMS where depth requires." },
      },
      notSure: { hazardsOn: ["haz-excavation-excavation-or-trench-collapse"], failCheck: "Trench depth and support requirement confirmed" },
    },
    {
      key: "testing",
      label: "Will the completed run be air or water tested before backfilling?",
      kind: "yesno",
      effects: {
        yes: { scopeLine: "Completed run air/water tested to BS EN 1610 before backfilling, with the result recorded against the run." },
        no: { hazardsOff: ["haz-drainage-compressed-air-release-during-pipe-testing"], failCheck: "Drainage testing to BS EN 1610 before backfill confirmed" },
      },
      notSure: { failCheck: "Drainage testing to BS EN 1610 before backfill confirmed" },
    },
    {
      key: "biological",
      label: "Who confirms the Weil's disease controls and wash facilities are in place?",
      kind: "text",
      placeholder: "e.g. site supervisor; wash-down unit on site, awareness cards issued",
      requiredCheck: "Weil's disease controls and wash facilities owner recorded",
      effects: { "*": { scopeLine: "Biological controls owner: {answer}. Cuts covered, wash before eating/drinking/smoking, Leptospira awareness cards issued." } },
    },
  ],

  "underground-services": [
    {
      key: "gas",
      label: "May gas mains be present in the dig area?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: ["haz-excavation-rupturing-a-gas-main-fire-explosion-or-release"] },
        no: { hazardsOff: ["haz-excavation-rupturing-a-gas-main-fire-explosion-or-release"], emergencyOff: ["Gas main strike"], scopeLine: "No gas mains in the dig area — confirmed against utility records; gas-strike controls are not required for this task." },
      },
      notSure: { hazardsOn: ["haz-excavation-rupturing-a-gas-main-fire-explosion-or-release"], failCheck: "Gas-main presence confirmed against utility records" },
    },
    {
      key: "water",
      label: "May water mains or other pressurised services be present?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: ["haz-excavation-striking-pressurised-water-or-service-causing-flooding"] },
        no: { hazardsOff: ["haz-excavation-striking-pressurised-water-or-service-causing-flooding"], emergencyOff: ["Water main strike"], scopeLine: "No water mains or pressurised services in the dig area — confirmed against utility records." },
      },
      notSure: { hazardsOn: ["haz-excavation-striking-pressurised-water-or-service-causing-flooding"], failCheck: "Water-main / pressurised service presence confirmed" },
    },
    {
      key: "hv",
      label: "May high-voltage cables be present in the dig area?",
      kind: "yesno",
      effects: {
        yes: { scopeLine: "HV cables may be present — arc-rated/flame-resistant clothing worn and a greater stand-off applied per the asset owner's requirements." },
        no: { scopeLine: "No HV cables expected — confirmed against utility records; standard insulated-tool stand-off applies." },
      },
      notSure: { failCheck: "High-voltage cable presence confirmed with the asset owner" },
    },
    {
      key: "plant",
      label: "Will an excavator be used to remove overburden near services?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [PLANT_PEDESTRIAN] },
        no: { hazardsOff: [PLANT_PEDESTRIAN], scopeLine: "Hand digging and vacuum excavation only — no excavator working near services for this task." },
      },
      notSure: { hazardsOn: [PLANT_PEDESTRIAN], failCheck: "Excavator use near services confirmed" },
    },
    {
      key: "permitToDig",
      label: "Who issues the permit to dig and records the survey and scan results?",
      kind: "text",
      placeholder: "e.g. PC site office, HSG47-competent issuer, per excavation",
      requiredCheck: "Permit-to-dig issuer and survey/scan record confirmed",
      effects: { "*": { scopeLine: "Permit to dig: {answer}. Issued per excavation, capturing the survey, CAT/Genny scan results and safe-dig precautions." } },
    },
    {
      key: "trialHoles",
      label: "Will trial holes be hand-dug to prove the position of located services?",
      kind: "yesno",
      effects: {
        yes: { scopeLine: "Trial holes hand-dug or vacuum-excavated to prove the position and depth of each located service before machine digging." },
        no: { failCheck: "Trial-hole proving of located services confirmed" },
      },
      notSure: { failCheck: "Trial-hole proving of located services confirmed" },
    },
  ],
};
